/**
 * Резервная копия данных.
 *
 * До этого модуля всё, что человек накопил, существовало ровно в одном месте —
 * в IndexedDB этого браузера. Очистка данных сайта, переустановка приложения,
 * смена устройства или вытеснение хранилища под давлением на диск означали
 * потерю без предупреждения и без возможности восстановления. Для замеров
 * давления и журнала приёмов лекарств это дороже, чем для заметок: такие
 * записи ведут месяцами и показывают врачу.
 *
 * Формат — обычный JSON: его можно открыть чем угодно, положить в облако,
 * отправить себе письмом. Читаемость здесь важнее компактности.
 */

import { db } from '@/shared/db/database';
import { countExtraRecords, dumpExtraDatabases, type ExtraDatabases } from '@/shared/db/extra-databases';
import type { BaseEntity } from '@/shared/db/schema';

import { checksumOf, payloadOf } from './integrity';

/** Версия формата — на случай, если состав данных изменится. */
export const BACKUP_FORMAT_VERSION = 1;

export interface Backup {
  format: number;
  /** Версия схемы базы: по ней видно, из какого приложения снята копия. */
  schemaVersion: number;
  exportedAt: string;
  userId: string;
  /**
   * Контрольная сумма данных и число записей.
   *
   * Копия не жалуется, когда записалась наполовину: файл лежит с приличным
   * именем и правдоподобным размером, а узнают об этом в момент, когда данных
   * уже нет. Эти два поля позволяют заметить порчу — и сразу после записи, и
   * перед восстановлением. У копий из старых версий их нет, и это не беда.
   */
  checksum?: string;
  records?: number;
  tables: Record<string, unknown[]>;
  /**
   * Базы съёмных разделов: «Беременность» и всё, что появится после неё.
   *
   * Такой раздел держит данные отдельно от основной схемы — и раньше они в
   * копию не попадали вовсе. Человек переносил данные на новое устройство и
   * обнаруживал пропажу тогда, когда со старого уже нечего взять.
   */
  databases?: ExtraDatabases;
}

/**
 * Таблицы, которые в копию не идут.
 *
 * `users` — учётные записи устройства целиком, включая чужие; выгружать их в
 * файл нельзя. `outbox` — очередь несинхронизированных изменений, она про
 * доставку, а не про данные. `settings` — настройки самого устройства.
 * `backups` — автоматические снимки: копия внутри копии удваивает файл каждый
 * день и не добавляет ни одной новой записи.
 */
const SKIPPED_TABLES = new Set(['users', 'outbox', 'settings', 'backups']);

/**
 * Собирает всё, что принадлежит пользователю.
 *
 * Мягко удалённые записи тоже попадают в копию: они ещё могут понадобиться
 * синхронизации, а для человека это лишь строки, которых он не увидит.
 */
export async function buildBackup(userId: string): Promise<Backup> {
  const tables: Record<string, unknown[]> = {};

  for (const table of db.tables) {
    if (SKIPPED_TABLES.has(table.name)) continue;

    const rows = (await table.where('userId').equals(userId).toArray()) as BaseEntity[];
    tables[table.name] = rows;
  }

  const databases = await dumpExtraDatabases(userId);

  const backup: Backup = {
    format: BACKUP_FORMAT_VERSION,
    schemaVersion: db.verno,
    exportedAt: new Date().toISOString(),
    userId,
    tables,
    // Пустое поле в файл не пишется: копия без съёмных разделов должна
    // остаться такой же, какой была, — вместе со своей контрольной суммой.
    ...(Object.keys(databases).length > 0 ? { databases } : {}),
  };

  backup.records = countBackupRecords(backup);
  backup.checksum = checksumOf(payloadOf(backup));

  return backup;
}

/** Сколько записей в копии — по этому числу человек понимает, что выгрузилось. */
export function countBackupRecords(backup: Backup): number {
  const main = Object.values(backup.tables).reduce((sum, rows) => sum + rows.length, 0);
  return main + countExtraRecords(backup.databases);
}

/** Имя файла с датой: копии складываются в папку и сами выстраиваются по порядку. */
export function backupFileName(exportedAt: string = new Date().toISOString()): string {
  return `kairo-backup-${exportedAt.slice(0, 10)}.json`;
}
